import { useMemo } from 'react';
import { useData } from '@/contexts/DataContext';
import GlobalFilters from '@/components/GlobalFilters';
import { computeKpis, topNByField } from '@/utils/kpiCalculations';
import { fmtEur } from '@/utils/formatters';
import { useAuth } from '@/contexts/AuthContext';
import {
  Loader2, Euro, Timer, Hourglass, Pause, PlayCircle, CheckCircle2, AlertTriangle,
} from 'lucide-react';

const colorMap = {
  blue:   { bg: 'bg-blue-50',           iconText: 'text-fw-blue' },
  green:  { bg: 'bg-green-50',          iconText: 'text-green-700' },
  amber:  { bg: 'bg-yellow-50',         iconText: 'text-fw-yellow-hover' },
  purple: { bg: 'bg-fw-purple-subtle',  iconText: 'text-fw-purple' },
  slate:  { bg: 'bg-fw-light',          iconText: 'text-fw-muted' },
  red:    { bg: 'bg-red-50',            iconText: 'text-red-600' },
};

function KpiCard({ title, value, subtitle, footer, icon: Icon, color = 'blue', testId }) {
  const c = colorMap[color] || colorMap.blue;
  return (
    <div
      className="text-center p-4 bg-white rounded-lg border border-fw-border border-l-4 border-l-fw-yellow shadow-sm"
      data-testid={testId}
    >
      <div className="flex justify-center mb-2">
        <Icon className={`w-5 h-5 ${c.iconText}`} />
      </div>
      <p className="text-xs text-fw-muted uppercase mb-1">{title}</p>
      <p className="font-heading text-2xl font-bold text-fw-dark">{value}</p>
      {subtitle && <p className="text-sm text-fw-muted mt-1">{subtitle}</p>}
      {footer && <p className="text-sm font-semibold text-fw-red mt-1">{footer}</p>}
    </div>
  );
}

function MiniTable({ title, data, testId }) {
  return (
    <div className="bg-white rounded-lg border border-fw-border shadow-sm overflow-hidden" data-testid={testId}>
      <div className="px-5 py-3 bg-fw-dark">
        <h3 className="text-sm font-semibold text-white">{title}</h3>
      </div>
      <div className="divide-y divide-fw-border/50">
        {data.length === 0 && (
          <div className="px-5 py-4 text-sm text-fw-subtle">Nessun dato disponibile</div>
        )}
        {data.map(({ name, count }, i) => (
          <div key={name} className="flex items-center justify-between px-5 py-3 hover:bg-fw-light transition-colors duration-150">
            <div className="flex items-center gap-3">
              <span className="w-6 h-6 rounded-full bg-fw-light text-xs font-semibold text-fw-muted flex items-center justify-center">{i + 1}</span>
              <span className="text-sm font-bold text-fw-dark uppercase">{name}</span>
            </div>
            <span className="text-sm font-bold text-fw-dark">{count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { getFilteredPractices, loading } = useData();
  const { role } = useAuth();

  const filtered = useMemo(() => getFilteredPractices(), [getFilteredPractices]);
  const kpis = useMemo(() => computeKpis(filtered), [filtered]);
  const topTipologie = useMemo(() => topNByField(filtered, 'tipologiaLavorazione', 5), [filtered]);
  const topStudi = useMemo(() => topNByField(filtered, 'studioProgettazione', 5), [filtered]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20" data-testid="dashboard-loading">
        <Loader2 className="w-8 h-8 text-fw-yellow animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div className="rounded-lg p-[3px]" style={{background: 'linear-gradient(to right, #FDC400, #E60000)'}}>
        <div className="bg-fw-dark rounded-[5px] px-6 py-5">
          <h1 className="font-heading text-3xl font-bold text-white">Dashboard</h1>
          <p className="text-fw-subtle mt-1">Panoramica delle attività e KPI principali</p>
        </div>
      </div>

      <GlobalFilters />

      {/* Griglia: 4 colonne per HQ/Fastweb (8 card = 2 righe piene), 3 per
          professionista e Ditta (6 card = 2 righe piene) — simmetria per ruolo. */}
      <div className={`grid grid-cols-1 sm:grid-cols-2 ${role === 'fornitore' || role === 'ditta' ? 'lg:grid-cols-3' : 'lg:grid-cols-4'} gap-4`} data-testid="kpi-cards-grid">
        {role !== 'ditta' && (
          <KpiCard
            title="Attività con documenti non caricati"
            value={kpis.totalNonAvviate}
            icon={Pause}
            color="slate"
            testId="kpi-non-avviate"
          />
        )}
        {role !== 'ditta' && (
          <KpiCard
            title="Attività In Corso"
            value={kpis.totalInCorso}
            icon={PlayCircle}
            color="blue"
            testId="kpi-in-corso"
          />
        )}
        {role === 'ditta' && (
          <KpiCard
            title="Da Valutare"
            value={kpis.totalDaValutare}
            subtitle="Preventivi in attesa della tua decisione"
            icon={PlayCircle}
            color="blue"
            testId="kpi-da-valutare"
          />
        )}
        {role === 'ditta' && (
          <KpiCard
            title="In Rielaborazione"
            value={kpis.totalInRielaborazione}
            subtitle="Preventivi rifiutati, in attesa del professionista"
            icon={Pause}
            color="slate"
            testId="kpi-in-rielaborazione"
          />
        )}
        <KpiCard
          title="Attività Concluse"
          value={kpis.totalChiuse}
          icon={CheckCircle2}
          color="green"
          testId="kpi-chiuse"
        />
        <KpiCard
          title="Importo Totale Preventivi"
          value={fmtEur(kpis.importoTotale)}
          subtitle={kpis.importoMedio != null ? `Media per sito: ${fmtEur(kpis.importoMedio)}` : ''}
          icon={Euro}
          color="purple"
          testId="kpi-importo"
        />
        {role !== 'ditta' && (
          <KpiCard
            title="Tempo medio gestione pratica"
            value={<>{kpis.leadTimeMedio != null ? kpis.leadTimeMedio.toFixed(1) : '–'}<span className="text-base font-normal text-fw-muted ml-1.5">GG LAV.</span></>}
            subtitle="Riferito al solo controllo documentale, non al tempo totale di gestione"
            icon={Timer}
            color="amber"
            testId="kpi-leadtime"
          />
        )}
        <KpiCard
          title="Tempo medio gestione Ditta"
          value={<>{kpis.leadTimeDittaMedio != null ? kpis.leadTimeDittaMedio.toFixed(1) : '–'}<span className="text-base font-normal text-fw-muted ml-1.5">GG LAV.</span></>}
          footer={kpis.leadTimeDittaMedio != null ? `Attività gestite in più di 7gg: ${kpis.slaDittaSuperato}` : null}
          icon={Hourglass}
          color="amber"
          testId="kpi-leadtime-ditta"
        />
        {role !== 'fornitore' && (
          <KpiCard
            title="Tempo medio rilavorazione Studio"
            value={<>{kpis.rilavorazioneStudioMedia != null ? kpis.rilavorazioneStudioMedia.toFixed(1) : '–'}<span className="text-base font-normal text-fw-muted ml-1.5">GG LAV.</span></>}
            subtitle="Media per ciclo di bocciatura (doc + preventivo)"
            icon={Timer}
            color="amber"
            testId="kpi-rilavorazione-studio"
          />
        )}
        {role !== 'fornitore' && role !== 'ditta' && (
          <KpiCard
            title="Rifiuti"
            value={
              <span className="flex items-center justify-center gap-5">
                <span className="flex flex-col items-center">
                  <span>{kpis.rifiutiTecniciMedi != null ? kpis.rifiutiTecniciMedi.toFixed(2) : '–'}</span>
                  <span className="text-[10px] font-normal uppercase tracking-wider text-fw-muted">Tecnici</span>
                </span>
                <span className="w-px h-9 bg-fw-border" aria-hidden="true"></span>
                <span className="flex flex-col items-center">
                  <span>{kpis.rifiutiAmministrativiMedi != null ? kpis.rifiutiAmministrativiMedi.toFixed(2) : '–'}</span>
                  <span className="text-[10px] font-normal uppercase tracking-wider text-fw-muted">Amministrativi</span>
                </span>
              </span>
            }
            subtitle="Media errori per attività"
            icon={AlertTriangle}
            color="red"
            testId="kpi-errori"
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <MiniTable title="Top 5 Tipi di Intervento per Volume" data={topTipologie} testId="top-tipologie-table" />
        {role !== 'fornitore' && role !== 'ditta' && (
          <MiniTable title="Top 5 Studi di Progettazione per Volume" data={topStudi} testId="top-studi-table" />
        )}
      </div>
    </div>
  );
}
